import { checkExhausted } from "@cartographerio/util";
import lodash from "lodash";
import utcToZonedTime from "date-fns-tz/utcToZonedTime";
import format from "date-fns/format";
import { PropertyType, PropertyValue } from ".";
import { Bucket, LookupBucket, RangeBucket } from "./type";

export function isLookupBucket<A = PropertyValue>(
  bucket: Bucket<A>
): bucket is LookupBucket<A> {
  switch (bucket.type) {
    case "LookupBucket":
      return true;
    case "RangeBucket":
      return false;
    default:
      return checkExhausted(bucket);
  }
}

export function isRangeBucket<A = PropertyValue>(
  bucket: Bucket<A>
): bucket is RangeBucket<A> {
  switch (bucket.type) {
    case "LookupBucket":
      return false;
    case "RangeBucket":
      return true;
    default:
      return checkExhausted(bucket);
  }
}

export function bucketMinValue<A>(bucket: Bucket<A>): A | null {
  return isLookupBucket(bucket) ? bucket.value : bucket.minValue;
}

export function bucketMaxValue<A>(bucket: Bucket<A>): A | null {
  return isLookupBucket(bucket) ? bucket.value : bucket.maxValue;
}

export function valueLabel(
  value: PropertyValue,
  propertyType?: PropertyType
): string {
  switch (propertyType) {
    case undefined:
    case "boolean":
    case "number":
    case "string":
      return value == null ? "(blank)" : String(value);

    case "timestamp":
      return typeof value === "number"
        ? // TODO: Detect the correct locale from the browser
          format(utcToZonedTime(new Date(value), "Europe/London"), "dd/MM/yyyy")
        : "blank";

    default:
      return checkExhausted(propertyType);
  }
}

export function rangeLabel(
  minValue: PropertyValue,
  maxValue: PropertyValue,
  maxInclusive?: boolean,
  propertyType?: PropertyType
): string | null {
  const minLabel = minValue == null ? null : valueLabel(minValue, propertyType);
  const maxLabel = maxValue == null ? null : valueLabel(maxValue, propertyType);

  if (minLabel != null && maxLabel != null) {
    if (minLabel === maxLabel) {
      return `${minLabel}`;
    } else if (maxInclusive) {
      return `${minLabel} to ${maxLabel}`;
    } else {
      return `${minLabel} to <${maxLabel}`;
    }
  } else if (minLabel != null) {
    return `≥${minLabel}`;
  } else if (maxLabel != null) {
    if (maxInclusive) {
      return `≤${maxLabel}`;
    } else {
      return `<${maxLabel}`;
    }
  } else {
    return null;
  }
}

export function bucketLabel(
  bucket: Bucket,
  propertyType?: PropertyType
): string | null {
  if (isLookupBucket(bucket)) {
    return bucket.label ?? valueLabel(bucket.value, propertyType);
  } else {
    return (
      bucket.label ??
      rangeLabel(
        bucket.minValue,
        bucket.maxValue,
        bucket.maxInclusive,
        propertyType
      )
    );
  }
}

export function multiBucketLabel(
  buckets: Bucket[],
  propertyType?: PropertyType
): string | null {
  if (buckets.some(isLookupBucket)) {
    return buckets.map(bucket => bucketLabel(bucket, propertyType)).join(", ");
  } else {
    const minValues = buckets.map(bucketMinValue);
    const maxValues = buckets.map(bucketMaxValue);

    const minValue = minValues.includes(null)
      ? null
      : lodash.min(minValues) ?? null;

    const maxValue = maxValues.includes(null)
      ? null
      : lodash.max(minValues) ?? null;

    const maxInclusive = false;

    return rangeLabel(minValue, maxValue, maxInclusive, propertyType);
  }
}

export function bucketMatches<A>(bucket: Bucket<A>, value: A | null): boolean {
  switch (bucket.type) {
    case "LookupBucket": {
      return bucket.value == null ? value == null : bucket.value === value;
    }

    case "RangeBucket": {
      const minBound: boolean =
        bucket.minValue == null
          ? true
          : value != null && bucket.minValue <= value;

      const maxBound: boolean =
        bucket.maxValue == null
          ? true
          : bucket.maxInclusive
          ? value != null && bucket.maxValue >= value
          : value != null && bucket.maxValue > value;

      return minBound && maxBound;
    }

    default:
      return checkExhausted(bucket);
  }
}

export function findMatchingBucket<A>(
  buckets: Bucket<A>[],
  value: A | null
): Bucket<A> | null {
  return buckets.find(bucket => bucketMatches(bucket, value)) ?? null;
}

export function findMatchingBucketIndex<A>(
  buckets: Bucket<A>[],
  value: A | null
): number {
  return buckets.findIndex(bucket => bucketMatches(bucket, value));
}

export function findMatchingBuckets<A>(
  buckets: Bucket<A>[],
  values: A[]
): Bucket<A>[] {
  return buckets.filter(bucket =>
    values.some(value => bucketMatches(bucket, value))
  );
}
